import { TaskPriority, TaskStatus, ProjectStatus } from '../types';

// Date helpers
/**
 * The date a stored value names, or null when it names none. Exported so a caller that needs
 * more than the formatted text (which year, say) reads the value exactly as formatDate does.
 */
export function parseDate(ts: number | string | null | undefined): Date | null {
  if (!ts) return null;
  // A bare calendar day ("2026-10-05") is that day where the reader is. `new Date()` reads it
  // as UTC midnight, which west of Greenwich is the evening before.
  const day = typeof ts === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ts) : null;
  const date = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(ts);
  return isNaN(date.getTime()) ? null : date;
}

// Accepts null: the API returns null for optional dates that have been cleared, so
// narrowing this to `undefined` forced 17 call sites to cast.
export function formatDate(ts: number | string | null | undefined, format: 'short' | 'medium' | 'relative' = 'medium'): string {
  const date = parseDate(ts);
  if (!date) return '—';

  if (format === 'relative') {
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  }

  if (format === 'short') {
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  }

  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function isOverdue(dueDate: number | string | undefined): boolean {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

export function daysUntil(dueDate: number | string | undefined): number | null {
  if (!dueDate) return null;
  const diff = new Date(dueDate).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
}

// Color helpers
export const PRIORITY_COLOR: Record<TaskPriority, string> = {
  None: 'var(--priority-none)',
  Low: 'var(--priority-low)',
  Medium: 'var(--priority-medium)',
  High: 'var(--priority-high)',
  Critical: 'var(--priority-critical)',
};

export const STATUS_CLASS: Record<TaskStatus, string> = {
  'Open': 'task-open',
  'In Progress': 'task-inprogress',
  'In Review': 'task-inreview',
  'On Hold': 'task-onhold',
  'Completed': 'task-completed',
  'Cancelled': 'task-cancelled',
};

export const PROJECT_STATUS_CLASS: Record<ProjectStatus, string> = {
  'Active': 'status-active',
  'On Hold': 'status-onhold',
  'Completed': 'status-completed',
  'Archived': 'status-archived',
};

// String helpers
export function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export function truncate(str: string, len: number): string {
  return str.length > len ? str.slice(0, len) + '…' : str;
}

export function slugify(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

// Number helpers
export function formatHours(h: number, m = 0): string {
  const total = h + m / 60;
  if (total < 1) return `${Math.round(total * 60)}m`;
  return `${total.toFixed(1)}h`;
}

/** A running stopwatch's elapsed milliseconds as `HH:MM:SS`. Never negative. */
export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

export function formatCurrency(amount: number, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

/**
 * Avatar fills, keyed off the name so one person keeps one colour.
 *
 * Drawn from the design system's own ramps — the palette led with the retired brand teal,
 * which no longer means anything in the product. Variety is deliberate (it is what makes
 * one person's avatar distinguishable from another's), but every value is a -600/-700 step
 * so white initials stay legible on all of them.
 */
const AVATAR_COLORS = [
  '#444ce7', '#3538cd', '#2d31a6', '#05603a',
  '#027a48', '#6b4f08', '#912018', '#475467',
];

export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// Generate project ID prefix from portal name
export function projectPrefix(portal: string): string {
  return portal.substring(0, 2).toUpperCase();
}

// Validate email
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Deep clone
export function clone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export type AppUserRole = 'Owner' | 'Admin' | 'Manager' | 'Developer' | 'Viewer';

export const APP_USER_ROLES: AppUserRole[] = ['Owner', 'Admin', 'Manager', 'Developer', 'Viewer'];

/**
 * The current user's role, as the SERVER reported it. Nothing is inferred.
 *
 * It was called `inferUserRole` and it did infer: any address containing "admin" or
 * "owner" became Admin, "pm" became Manager, and a portal matching the email local part
 * became Owner — all trivially self-assigned by choosing an email. That guessing is gone,
 * and so are the `email` and `portalName` parameters it needed, because a function that
 * still accepts them is an invitation to start consulting them again.
 *
 * Dropping them was worth doing for a second reason: it turned every call site that had
 * quietly stopped passing a role into a compile error. Several had, and because an
 * unknown role resolves to the LEAST privileged one, they were silently gating every
 * Owner and Admin as a Viewer — which removed Reports, Approvals and Setup from the
 * sidebar and redirected away from those routes entirely.
 *
 * Failing closed is still right: a role that cannot be read must never widen access.
 * This only gates UI affordances; the server enforces permissions independently.
 */
export function serverRole(currentRole?: string): AppUserRole {
  const normalized = String(currentRole || '').trim() as AppUserRole;
  return APP_USER_ROLES.includes(normalized) ? normalized : 'Viewer';
}

// `financials` is Owner/Admin/Manager only, matching the MANAGE tier the reports are
// gated at server-side. This map is convenience, not security — the server answers 403
// regardless — but a nav entry that leads to one is a broken link, not a safeguard.
export const ROLE_ACCESS: Record<AppUserRole, string[]> = {
  // `workload` (who is assigned what, and its stage) is Owner/Admin only — a management view.
  Owner: ['home', 'reports', 'performance', 'financials', 'workload', 'projects', 'collaboration', 'approvals', 'timesheets', 'all-tasks', 'all-issues', 'phases', 'timelogs', 'settings', 'setup'],
  Admin: ['home', 'reports', 'performance', 'financials', 'workload', 'projects', 'collaboration', 'approvals', 'timesheets', 'all-tasks', 'all-issues', 'phases', 'timelogs', 'settings', 'setup'],
  Manager: ['home', 'reports', 'performance', 'financials', 'projects', 'collaboration', 'approvals', 'timesheets', 'all-tasks', 'all-issues', 'phases', 'timelogs', 'settings', 'setup'],
  Developer: ['home', 'projects', 'collaboration', 'timesheets', 'all-tasks', 'all-issues', 'phases', 'timelogs', 'settings', 'setup'],
  Viewer: ['home', 'projects', 'collaboration', 'timesheets', 'all-tasks', 'all-issues', 'phases', 'timelogs', 'settings'],
};

export function canAccessPortalRoute(route: string, currentRole?: string): boolean {
  const role = serverRole(currentRole);
  const normalized = route.replace(/^\/portal\/?/, '').split('/')[0] || 'home';
  const key = normalized === 'project' ? 'projects' : normalized === 'dashboard' ? 'home' : normalized;
  return ROLE_ACCESS[role].includes(key);
}

export function getLandingStorageKey(email?: string): string {
  return `pm_landing_page_${(email || 'guest').toLowerCase()}`;
}

export function getStoredLandingPage(email?: string): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(getLandingStorageKey(email));
}

export function getDefaultPortalRoute(preferredLanding?: string | null, currentRole?: string): string {
  const normalizedLanding = (preferredLanding || '').replace(/^"|"$/g, '').toLowerCase();
  const landingMap: Record<string, string> = {
    projects: '/portal/projects',
    home: '/portal/home',
    tasks: '/portal/all-tasks',
    reports: '/portal/reports',
    timesheets: '/portal/timesheets',
    collaboration: '/portal/collaboration',
  };

  if (
    normalizedLanding &&
    normalizedLanding !== 'auto' &&
    landingMap[normalizedLanding] &&
    canAccessPortalRoute(landingMap[normalizedLanding], currentRole)
  ) {
    return landingMap[normalizedLanding];
  }

  const role = serverRole(currentRole);
  if (role === 'Owner' || role === 'Admin') return '/portal/projects';
  if (role === 'Manager') return '/portal/projects';
  return '/portal/home';
}

export function downloadTextFile(filename: string, content: string, mime = 'text/plain;charset=utf-8') {
  if (typeof window === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Group array by key
export function groupBy<T>(arr: T[], key: keyof T): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key]);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}
